import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';

const TIPOS = [
    { value: 'entrada',    label: 'Entrada',    desc: 'Onde leads novos chegam',     color: '#3b82f6', icon: 'sign-in-alt' },
    { value: 'normal',     label: 'Normal',     desc: 'Estágio intermediário',        color: '#8b5cf6', icon: 'circle' },
    { value: 'convertido', label: 'Convertido', desc: 'Lead virou paciente',          color: '#22c55e', icon: 'check-circle' },
    { value: 'perdido',    label: 'Perdido',    desc: 'Lead não convertido',          color: '#ef4444', icon: 'times-circle' },
];

const CORES_PRESET = [
    '#3b82f6', '#8b5cf6', '#ec4899', '#f97316',
    '#eab308', '#22c55e', '#14b8a6', '#6b7280',
    '#ef4444', '#0ea5e9', '#a855f7', '#f59e0b',
];

const PipelineEditor = ({ show, pipeline, onClose, onSave }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [erro, setErro] = useState(null);

    const [nome, setNome] = useState('');
    const [descricao, setDescricao] = useState('');
    const [cor, setCor] = useState('#0d6efd');
    const [isDefault, setIsDefault] = useState(false);
    const [estagios, setEstagios] = useState([]);

    const [connections, setConnections] = useState([]);
    const [selectedConnectionIds, setSelectedConnectionIds] = useState([]);
    const [isLoadingConnections, setIsLoadingConnections] = useState(false);

    useEffect(() => {
        if (show) {
            setTimeout(() => setIsVisible(true), 10);

            if (pipeline) {
                setNome(pipeline.nome || '');
                setDescricao(pipeline.descricao || '');
                setCor(pipeline.cor || '#0d6efd');
                setIsDefault(pipeline.is_default || false);
                setEstagios(pipeline.estagios?.map(e => ({ ...e })) || []);
                setSelectedConnectionIds(
                    (pipeline.connections || []).map(c => c.id)
                );
            } else {
                setNome('');
                setDescricao('');
                setCor('#0d6efd');
                setIsDefault(false);
                setSelectedConnectionIds([]);
                setEstagios([
                    { id: null, nome: 'Novos Leads',        cor: '#0d6efd', ordem: 1, tipo: 'entrada' },
                    { id: null, nome: 'Primeiro Contato',   cor: '#6f42c1', ordem: 2, tipo: 'normal' },
                    { id: null, nome: 'Em Negociação',       cor: '#fd7e14', ordem: 3, tipo: 'normal' },
                    { id: null, nome: 'Aguardando Decisão', cor: '#ffc107', ordem: 4, tipo: 'normal' },
                    { id: null, nome: 'Convertido',         cor: '#198754', ordem: 5, tipo: 'convertido' },
                    { id: null, nome: 'Perdido',            cor: '#6c757d', ordem: 6, tipo: 'perdido' },
                ]);
            }

            setIsLoadingConnections(true);
            axios.get('/api/connections')
                .then(r => setConnections(r.data.connections || []))
                .catch(() => {})
                .finally(() => setIsLoadingConnections(false));

            setErro(null);
        } else {
            setIsVisible(false);
        }
    }, [show, pipeline]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => onClose(), 300);
    };

    const handleToggleConnection = (connectionId) => {
        setSelectedConnectionIds(prev =>
            prev.includes(connectionId)
                ? prev.filter(id => id !== connectionId)
                : [...prev, connectionId]
        );
    };

    const handleAdicionarEstagio = () => {
        const novoEstagio = {
            id: null, nome: 'Novo Estágio',
            cor: '#6b7280', ordem: estagios.length + 1, tipo: 'normal',
        };
        const indexConvertido = estagios.findIndex(e => e.tipo === 'convertido');
        if (indexConvertido > -1) {
            const novos = [...estagios];
            novos.splice(indexConvertido, 0, novoEstagio);
            setEstagios(novos.map((e, i) => ({ ...e, ordem: i + 1 })));
        } else {
            setEstagios([...estagios, novoEstagio]);
        }
    };

    const handleRemoverEstagio = (index) => {
        const estagio = estagios[index];
        if (['entrada', 'convertido', 'perdido'].includes(estagio.tipo)) {
            setErro(`O estágio "${estagio.nome}" é obrigatório e não pode ser removido.`);
            return;
        }
        setEstagios(prev => prev.filter((_, i) => i !== index).map((e, i) => ({ ...e, ordem: i + 1 })));
    };

    const handleAlterarEstagio = (index, campo, valor) => {
        setEstagios(prev => prev.map((e, i) => i === index ? { ...e, [campo]: valor } : e));
    };

    const handleMoverEstagio = (index, direcao) => {
        const novoIndex = index + direcao;
        if (novoIndex < 0 || novoIndex >= estagios.length) return;
        const estagio = estagios[index];
        const alvo = estagios[novoIndex];
        if (estagio.tipo === 'entrada' && direcao > 0 && alvo.tipo !== 'normal') return;
        if (estagio.tipo === 'convertido' || estagio.tipo === 'perdido') return;
        if (alvo.tipo === 'entrada' && direcao < 0) return;
        const novos = [...estagios];
        [novos[index], novos[novoIndex]] = [novos[novoIndex], novos[index]];
        setEstagios(novos.map((e, i) => ({ ...e, ordem: i + 1 })));
    };

    const validar = () => {
        if (!nome.trim()) return 'O nome da pipeline é obrigatório.';
        const tipos = estagios.map(e => e.tipo);
        if (tipos.filter(t => t === 'entrada').length !== 1) return 'A pipeline deve ter exatamente 1 estágio de entrada.';
        if (tipos.filter(t => t === 'convertido').length !== 1) return 'A pipeline deve ter exatamente 1 estágio convertido.';
        if (tipos.filter(t => t === 'perdido').length !== 1) return 'A pipeline deve ter exatamente 1 estágio perdido.';
        if (estagios.some(e => !e.nome.trim())) return 'Todos os estágios precisam ter um nome.';
        return null;
    };

    const handleSalvar = async () => {
        const erroValidacao = validar();
        if (erroValidacao) { setErro(erroValidacao); return; }

        setIsSaving(true);
        setErro(null);

        const payload = {
            nome, descricao, cor,
            is_default: isDefault,
            connection_ids: selectedConnectionIds,
            estagios: estagios.map((e, i) => ({
                id:    e.id || undefined,
                nome:  e.nome,
                cor:   e.cor,
                ordem: i + 1,
                tipo:  e.tipo,
            })),
        };

        try {
            if (pipeline?.id) {
                await axios.put(`/api/pipelines/${pipeline.id}`, payload);
            } else {
                await axios.post('/api/pipelines', payload);
            }
            onSave();
        } catch (err) {
            setErro(err.response?.data?.message || 'Erro ao salvar a pipeline.');
        } finally {
            setIsSaving(false);
        }
    };

    // ← Retorna a cor baseada no is_active e status
    const getStatusColor = (connection) => {
        if (!connection.is_active) return '#9ca3af';       // Cinza = inativa
        if (connection.status === 'online') return '#22c55e';    // Verde = online
        if (connection.status === 'connecting') return '#f59e0b'; // Amarelo = conectando
        return '#f97316';                                         // Laranja = ativa mas offline
    };

    // ← Retorna o label baseado no is_active e status
    const getStatusLabel = (connection) => {
        if (!connection.is_active) return 'Inativa';
        if (connection.status === 'online') return 'Online';
        if (connection.status === 'connecting') return 'Conectando';
        return 'Ativa / Offline';
    };

    const getChannelIcon = (channel) => {
        if (channel === 'whatsapp') return <FontAwesomeIcon icon={faWhatsapp} style={{ color: '#25d366' }} />;
        if (channel === 'instagram') return <FontAwesomeIcon icon="camera" style={{ color: '#e1306c' }} />;
        return <FontAwesomeIcon icon="plug" style={{ color: '#6b7280' }} />;
    };

    if (!show) return null;

    return (
        <>
            {/* Overlay */}
            <div
                onClick={handleClose}
                style={{
                    position: 'fixed', inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 1060,
                    opacity: isVisible ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                }}
            />

            {/* Drawer */}
            <div style={{
                position: 'fixed',
                top: 0, right: 0, bottom: 0,
                width: '100%', maxWidth: '720px',
                backgroundColor: '#fff',
                zIndex: 1070,
                display: 'flex', flexDirection: 'column',
                transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.3s ease',
                boxShadow: '-4px 0 24px rgba(0,0,0,0.2)',
            }}>
                {/* Botão fechar */}
                <button
                    onClick={handleClose}
                    style={{
                        position: 'absolute', top: '24px', left: '-18px',
                        width: '36px', height: '36px', borderRadius: '50%',
                        backgroundColor: '#fff', border: '1px solid #e5e7eb',
                        cursor: 'pointer', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 10,
                    }}
                >
                    <FontAwesomeIcon icon="times" style={{ fontSize: `calc(14px * var(--font-scale))`, color: '#374151' }} />
                </button>

                {/* Header */}
                <div style={{
                    padding: '24px 28px 20px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>
                            {pipeline ? 'Editar Pipeline' : 'Nova Pipeline'}
                        </h2>
                        <p style={{ margin: '4px 0 0', fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280' }}>
                            Configure o funil e os estágios da jornada do lead
                        </p>
                    </div>
                    <button
                        onClick={handleSalvar}
                        disabled={isSaving}
                        style={{
                            padding: '10px 20px', backgroundColor: '#3b82f6',
                            color: '#fff', border: 'none', borderRadius: '8px',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            fontSize: `calc(14px * var(--font-scale))`, fontWeight: '600',
                            display: 'flex', alignItems: 'center', gap: '8px',
                            opacity: isSaving ? 0.7 : 1,
                        }}
                    >
                        {isSaving ? <Spinner as="span" size="sm" animation="border" /> : <FontAwesomeIcon icon="save" />}
                        Salvar Pipeline
                    </button>
                </div>

                {/* Conteúdo */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

                    {/* Erro */}
                    {erro && (
                        <div style={{
                            backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                            borderRadius: '8px', padding: '12px 16px',
                            color: '#dc2626', fontSize: `calc(13px * var(--font-scale))`, marginBottom: '20px',
                            display: 'flex', alignItems: 'center', gap: '8px',
                        }}>
                            <FontAwesomeIcon icon="exclamation-circle" />
                            {erro}
                        </div>
                    )}

                    {/* Configurações gerais */}
                    <div style={{
                        backgroundColor: '#f9fafb', borderRadius: '12px',
                        padding: '20px', marginBottom: '24px',
                        border: '1px solid #e5e7eb',
                    }}>
                        <h3 style={{ margin: '0 0 16px', fontSize: `calc(14px * var(--font-scale))`, fontWeight: '600', color: '#374151' }}>
                            Configurações Gerais
                        </h3>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            {/* Nome */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                                    Nome da Pipeline *
                                </label>
                                <input
                                    value={nome}
                                    onChange={(e) => setNome(e.target.value)}
                                    placeholder="Ex: Clínica Principal, Filial Asa Norte..."
                                    style={{
                                        width: '100%', padding: '10px 14px',
                                        border: '1px solid #e5e7eb', borderRadius: '8px',
                                        fontSize: `calc(14px * var(--font-scale))`, outline: 'none',
                                    }}
                                    onFocus={e => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                                />
                            </div>

                            {/* Descrição */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                                    Descrição
                                </label>
                                <input
                                    value={descricao}
                                    onChange={(e) => setDescricao(e.target.value)}
                                    placeholder="Descrição opcional..."
                                    style={{
                                        width: '100%', padding: '10px 14px',
                                        border: '1px solid #e5e7eb', borderRadius: '8px',
                                        fontSize: `calc(14px * var(--font-scale))`, outline: 'none',
                                    }}
                                />
                            </div>

                            {/* Cor */}
                            <div>
                                <label style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                                    Cor de Identificação
                                </label>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {CORES_PRESET.map(c => (
                                        <div
                                            key={c}
                                            onClick={() => setCor(c)}
                                            style={{
                                                width: '28px', height: '28px', borderRadius: '50%',
                                                backgroundColor: c, cursor: 'pointer',
                                                border: cor === c ? '3px solid #111827' : '2px solid transparent',
                                                transition: 'border 0.15s',
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Pipeline padrão */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div
                                    onClick={() => setIsDefault(!isDefault)}
                                    style={{
                                        width: '40px', height: '22px', borderRadius: '999px',
                                        backgroundColor: isDefault ? '#3b82f6' : '#d1d5db',
                                        cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                                        flexShrink: 0,
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute', top: '2px',
                                        left: isDefault ? '20px' : '2px',
                                        width: '18px', height: '18px', borderRadius: '50%',
                                        backgroundColor: '#fff', transition: 'left 0.2s',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#374151' }}>
                                        Pipeline Padrão
                                    </div>
                                    <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>
                                        Leads novos serão criados aqui
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Seção de Conexões */}
                    <div style={{
                        backgroundColor: '#f9fafb', borderRadius: '12px',
                        padding: '20px', marginBottom: '24px',
                        border: '1px solid #e5e7eb',
                    }}>
                        <h3 style={{ margin: '0 0 4px', fontSize: `calc(14px * var(--font-scale))`, fontWeight: '600', color: '#374151' }}>
                            Conexões Vinculadas
                        </h3>
                        <p style={{ margin: '0 0 16px', fontSize: `calc(12px * var(--font-scale))`, color: '#9ca3af' }}>
                            Selecione quais conexões alimentam esta pipeline. Uma conexão só pode pertencer a uma pipeline.
                        </p>

                        {isLoadingConnections ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <Spinner animation="border" size="sm" variant="primary" />
                            </div>
                        ) : connections.length === 0 ? (
                            <div style={{
                                textAlign: 'center', padding: '20px',
                                backgroundColor: '#fff', borderRadius: '8px',
                                border: '2px dashed #e5e7eb', color: '#9ca3af', fontSize: `calc(13px * var(--font-scale))`,
                            }}>
                                <FontAwesomeIcon icon="plug" style={{ fontSize: '24px', marginBottom: '8px', display: 'block' }} />
                                Nenhuma conexão cadastrada.
                                <br />
                                <span style={{ fontSize: `calc(11px * var(--font-scale))` }}>Configure conexões em Configurações → Conexões</span>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {connections.map(connection => {
                                    const isSelected = selectedConnectionIds.includes(connection.id);
                                    const isVinculadaOutra = connection.pipeline_id &&
                                        connection.pipeline_id !== pipeline?.id;
                                    const podeSelecionar = !isVinculadaOutra;
                                    const statusColor = getStatusColor(connection);
                                    const statusLabel = getStatusLabel(connection);

                                    return (
                                        <div
                                            key={connection.id}
                                            onClick={() => podeSelecionar && handleToggleConnection(connection.id)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '12px',
                                                padding: '12px 16px', borderRadius: '10px',
                                                border: `2px solid ${isSelected ? '#3b82f6' : '#e5e7eb'}`,
                                                backgroundColor: isSelected ? '#eff6ff' : isVinculadaOutra ? '#f9fafb' : '#fff',
                                                cursor: podeSelecionar ? 'pointer' : 'not-allowed',
                                                opacity: isVinculadaOutra ? 0.5 : 1,
                                                transition: 'all 0.15s',
                                            }}
                                        >
                                            {/* Checkbox visual */}
                                            <div style={{
                                                width: '20px', height: '20px', borderRadius: '6px',
                                                border: `2px solid ${isSelected ? '#3b82f6' : '#d1d5db'}`,
                                                backgroundColor: isSelected ? '#3b82f6' : '#fff',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0, transition: 'all 0.15s',
                                            }}>
                                                {isSelected && (
                                                    <FontAwesomeIcon icon="check" style={{ fontSize: `calc(10px * var(--font-scale))`, color: '#fff' }} />
                                                )}
                                            </div>

                                            {/* Ícone do canal */}
                                            <div style={{
                                                width: '36px', height: '36px', borderRadius: '8px',
                                                backgroundColor: '#f3f4f6',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '16px', flexShrink: 0,
                                            }}>
                                                {getChannelIcon(connection.channel)}
                                            </div>

                                            {/* Info */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#374151' }}>
                                                    {connection.name}
                                                </div>
                                                <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af', display: 'flex', gap: '8px', marginTop: '2px', flexWrap: 'wrap' }}>
                                                    <span style={{ textTransform: 'capitalize' }}>{connection.channel}</span>
                                                    <span>·</span>
                                                    <span style={{ textTransform: 'capitalize' }}>{connection.provider}</span>
                                                    {isVinculadaOutra && (
                                                        <>
                                                            <span>·</span>
                                                            <span style={{ color: '#f97316' }}>
                                                                <FontAwesomeIcon icon="exclamation-triangle" style={{ marginRight: '3px' }} />
                                                                Vinculada a outra pipeline
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Status — baseado em is_active + status */}
                                            <div style={{
                                                display: 'flex', alignItems: 'center', gap: '4px',
                                                fontSize: `calc(11px * var(--font-scale))`, fontWeight: '500',
                                                color: statusColor,
                                                flexShrink: 0,
                                            }}>
                                                <div style={{
                                                    width: '6px', height: '6px', borderRadius: '50%',
                                                    backgroundColor: statusColor,
                                                }} />
                                                {statusLabel}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Estágios */}
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: `calc(14px * var(--font-scale))`, fontWeight: '600', color: '#374151' }}>
                                    Estágios do Kanban
                                </h3>
                                <p style={{ margin: '4px 0 0', fontSize: `calc(12px * var(--font-scale))`, color: '#9ca3af' }}>
                                    Configure a jornada do lead. Estágios obrigatórios não podem ser removidos.
                                </p>
                            </div>
                            <button
                                onClick={handleAdicionarEstagio}
                                style={{
                                    padding: '8px 14px', backgroundColor: '#f0fdf4',
                                    color: '#16a34a', border: '1px solid #bbf7d0',
                                    borderRadius: '8px', cursor: 'pointer',
                                    fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                }}
                            >
                                <FontAwesomeIcon icon="plus" />
                                Adicionar Estágio
                            </button>
                        </div>

                        {/* Preview visual */}
                        <div style={{
                            backgroundColor: '#f9fafb', borderRadius: '10px',
                            padding: '16px', marginBottom: '20px',
                            border: '1px solid #e5e7eb', overflowX: 'auto',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 'max-content' }}>
                                {estagios.map((estagio, index) => (
                                    <React.Fragment key={index}>
                                        <div style={{
                                            padding: '6px 12px', borderRadius: '999px',
                                            backgroundColor: `${estagio.cor}22`,
                                            border: `1px solid ${estagio.cor}66`,
                                            fontSize: `calc(11px * var(--font-scale))`, fontWeight: '600',
                                            color: estagio.cor, whiteSpace: 'nowrap',
                                        }}>
                                            {estagio.nome}
                                        </div>
                                        {index < estagios.length - 1 && (
                                            <FontAwesomeIcon icon="arrow-right" style={{ color: '#d1d5db', fontSize: `calc(10px * var(--font-scale))` }} />
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>

                        {/* Lista de estágios */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {estagios.map((estagio, index) => (
                                <EstagioItem
                                    key={index}
                                    estagio={estagio}
                                    index={index}
                                    total={estagios.length}
                                    onChange={(campo, valor) => handleAlterarEstagio(index, campo, valor)}
                                    onRemover={() => handleRemoverEstagio(index)}
                                    onMover={(dir) => handleMoverEstagio(index, dir)}
                                />
                            ))}
                        </div>

                        {/* Legenda */}
                        <div style={{
                            marginTop: '20px', padding: '16px',
                            backgroundColor: '#fffbeb', borderRadius: '8px',
                            border: '1px solid #fde68a',
                        }}>
                            <div style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
                                <FontAwesomeIcon icon="info-circle" style={{ marginRight: '6px' }} />
                                Tipos de Estágio
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                {TIPOS.map(tipo => (
                                    <div key={tipo.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: `calc(11px * var(--font-scale))`, color: '#78716c' }}>
                                        <FontAwesomeIcon icon={tipo.icon} style={{ color: tipo.color, width: '14px' }} />
                                        <strong style={{ color: tipo.color }}>{tipo.label}:</strong> {tipo.desc}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

// ─── Item de Estágio ─────────────────────────────────────────────────────────
const EstagioItem = ({ estagio, index, total, onChange, onRemover, onMover }) => {
    const isObrigatorio = ['entrada', 'convertido', 'perdido'].includes(estagio.tipo);
    const tipoInfo = TIPOS.find(t => t.value === estagio.tipo);

    return (
        <div style={{
            backgroundColor: '#fff', borderRadius: '10px',
            border: `1px solid ${isObrigatorio ? `${tipoInfo?.color}44` : '#e5e7eb'}`,
            padding: '14px 16px',
            display: 'flex', alignItems: 'center', gap: '12px',
        }}>
            {/* Ordem / Mover */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                <button
                    onClick={() => onMover(-1)}
                    disabled={index === 0 || isObrigatorio}
                    style={{
                        background: 'none', border: 'none',
                        cursor: index === 0 || isObrigatorio ? 'default' : 'pointer',
                        color: index === 0 || isObrigatorio ? '#e5e7eb' : '#9ca3af',
                        padding: '2px', fontSize: `calc(10px * var(--font-scale))`,
                    }}
                >
                    <FontAwesomeIcon icon="chevron-up" />
                </button>
                <span style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af', textAlign: 'center', fontWeight: '600' }}>
                    {index + 1}
                </span>
                <button
                    onClick={() => onMover(1)}
                    disabled={index === total - 1 || isObrigatorio}
                    style={{
                        background: 'none', border: 'none',
                        cursor: index === total - 1 || isObrigatorio ? 'default' : 'pointer',
                        color: index === total - 1 || isObrigatorio ? '#e5e7eb' : '#9ca3af',
                        padding: '2px', fontSize: `calc(10px * var(--font-scale))`,
                    }}
                >
                    <FontAwesomeIcon icon="chevron-down" />
                </button>
            </div>

            {/* Cor */}
            <input
                type="color"
                value={estagio.cor}
                onChange={(e) => onChange('cor', e.target.value)}
                style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0,
                }}
            />

            {/* Nome */}
            <input
                value={estagio.nome}
                onChange={(e) => onChange('nome', e.target.value)}
                placeholder="Nome do estágio"
                style={{
                    flex: 1, padding: '8px 12px',
                    border: '1px solid #e5e7eb', borderRadius: '6px',
                    fontSize: `calc(13px * var(--font-scale))`, outline: 'none',
                }}
                onFocus={e => e.target.style.borderColor = '#3b82f6'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
            />

            {/* Tipo */}
            <select
                value={estagio.tipo}
                onChange={(e) => onChange('tipo', e.target.value)}
                disabled={isObrigatorio}
                style={{
                    padding: '8px 10px', borderRadius: '6px',
                    border: `1px solid ${tipoInfo?.color}44`,
                    backgroundColor: `${tipoInfo?.color}10`,
                    color: tipoInfo?.color, fontSize: `calc(12px * var(--font-scale))`,
                    fontWeight: '600', cursor: isObrigatorio ? 'not-allowed' : 'pointer',
                    outline: 'none',
                }}
            >
                {TIPOS.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                ))}
            </select>

            {/* Badge obrigatório / Botão remover */}
            {isObrigatorio ? (
                <span style={{
                    fontSize: `calc(10px * var(--font-scale))`, padding: '3px 8px', borderRadius: '999px',
                    backgroundColor: `${tipoInfo?.color}18`, color: tipoInfo?.color,
                    fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                    <FontAwesomeIcon icon="lock" style={{ marginRight: '4px', fontSize: '9px' }} />
                    Obrigatório
                </span>
            ) : (
                <button
                    onClick={onRemover}
                    style={{
                        background: 'none', border: 'none',
                        color: '#fca5a5', cursor: 'pointer',
                        padding: '6px', borderRadius: '6px', flexShrink: 0,
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fef2f2'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                    <FontAwesomeIcon icon="trash" style={{ fontSize: `calc(13px * var(--font-scale))` }} />
                </button>
            )}
        </div>
    );
};

export default PipelineEditor;
